import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, 
  Scan,
  MapPin,
  Wifi,
  Smartphone,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Globe,
  Radio,
  Bluetooth,
  Camera,
  Mic,
  Bell,
  TrendingUp,
  ExternalLink,
  ChevronRight,
  Loader2,
  Crown,
  Zap,
  RefreshCw,
  Info,
  Settings,
  FileWarning,
  Clock,
  Timer,
  BellRing,
  X,
  Plus,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CyberShield() {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [browserPermissions, setBrowserPermissions] = useState({});
  const [threats, setThreats] = useState([]);
  const [activeTab, setActiveTab] = useState('scan');
  
  // GPS Reminder state
  const [gpsReminderActive, setGpsReminderActive] = useState(false);
  const [gpsReminders, setGpsReminders] = useState([]);
  const [gpsReminderDuration, setGpsReminderDuration] = useState(30);
  const [gpsReminderNote, setGpsReminderNote] = useState('');
  const [settingReminder, setSettingReminder] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);

  // Known tracking apps database
  const trackingAppsDatabase = [
    { name: "Facebook", icon: "📘", risk: "high", trackers: ["Location", "Contacts", "Camera", "Microphone"], dataShared: ["Advertisers", "Data brokers", "Partner apps"], monthlyUsers: "3B+" },
    { name: "Instagram", icon: "📷", risk: "high", trackers: ["Location", "Camera", "Contacts", "Usage patterns"], dataShared: ["Meta platforms", "Advertisers"], monthlyUsers: "2B+" },
    { name: "TikTok", icon: "🎵", risk: "critical", trackers: ["Location", "Clipboard", "Device ID", "Keystroke patterns"], dataShared: ["ByteDance", "Advertisers", "Unknown third parties"], monthlyUsers: "1B+" },
    { name: "WhatsApp", icon: "💬", risk: "medium", trackers: ["Contacts", "Location (if shared)", "Usage metadata"], dataShared: ["Meta platforms"], monthlyUsers: "2B+" },
    { name: "Google Maps", icon: "🗺️", risk: "high", trackers: ["Precise location", "Location history", "Search history"], dataShared: ["Google services", "Advertisers"], monthlyUsers: "1B+" },
    { name: "Uber/Lyft", icon: "🚗", risk: "medium", trackers: ["Precise location", "Payment info", "Travel patterns"], dataShared: ["Drivers", "Insurance", "Marketing"], monthlyUsers: "100M+" },
    { name: "Weather Apps", icon: "🌤️", risk: "high", trackers: ["Background location", "Device ID", "Usage patterns"], dataShared: ["Data brokers", "Hedge funds", "Advertisers"], monthlyUsers: "Varies" },
    { name: "Shopping Apps", icon: "🛒", risk: "medium", trackers: ["Location", "Purchase history", "Browsing behavior"], dataShared: ["Retailers", "Advertisers", "Analytics"], monthlyUsers: "Varies" },
    { name: "Dating Apps", icon: "💕", risk: "critical", trackers: ["Precise location", "Photos", "Personal preferences", "Messages"], dataShared: ["Match Group", "Advertisers", "Data brokers"], monthlyUsers: "300M+" },
    { name: "Fitness Apps", icon: "🏃", risk: "high", trackers: ["Location", "Health data", "Daily routines", "Sleep patterns"], dataShared: ["Insurance companies", "Advertisers", "Research"], monthlyUsers: "500M+" },
  ];

  // Emerging cyber threats
  const emergingThreats = [
    {
      title: "Fog Reveal Surveillance Tool",
      description: "Government agencies purchasing location data from apps without warrants. Your phone's advertising ID can be used to track your movements.",
      severity: "critical",
      affected: "All smartphone users",
      solution: "Reset advertising ID monthly, disable location for non-essential apps"
    },
    {
      title: "Silent SMS Tracking",
      description: "Hidden SMS messages that can reveal your location to attackers without your knowledge.",
      severity: "high",
      affected: "All mobile users",
      solution: "Use Signal or encrypted messaging, be wary of unknown texts"
    },
    {
      title: "Wi-Fi Probe Requests",
      description: "Your device constantly broadcasts previously connected Wi-Fi networks, revealing your location history.",
      severity: "medium",
      affected: "All Wi-Fi enabled devices",
      solution: "Disable Wi-Fi when not in use, use MAC randomization"
    },
    {
      title: "Bluetooth Beacons",
      description: "Retail stores and malls track your movements using Bluetooth beacons and your device's Bluetooth signal.",
      severity: "medium",
      affected: "Bluetooth-enabled devices",
      solution: "Disable Bluetooth when not actively using it"
    },
    {
      title: "Cross-App Tracking",
      description: "Apps share your data through SDKs, building profiles across multiple services without consent.",
      severity: "high",
      affected: "All app users",
      solution: "Use privacy-focused apps, enable App Tracking Transparency on iOS"
    }
  ];

  useEffect(() => {
    checkBrowserPermissions();
    fetchThreats();
  }, []);

  const checkBrowserPermissions = async () => {
    const permissions = {};
    
    try {
      // Check Geolocation
      if ('geolocation' in navigator) {
        const geoStatus = await navigator.permissions.query({ name: 'geolocation' });
        permissions.location = geoStatus.state;
      }
      
      // Check Notifications
      if ('Notification' in window) {
        permissions.notifications = Notification.permission;
      }
      
      // Check Camera
      try {
        const cameraStatus = await navigator.permissions.query({ name: 'camera' });
        permissions.camera = cameraStatus.state;
      } catch { permissions.camera = 'unknown'; }
      
      // Check Microphone
      try {
        const micStatus = await navigator.permissions.query({ name: 'microphone' });
        permissions.microphone = micStatus.state;
      } catch { permissions.microphone = 'unknown'; }
      
      setBrowserPermissions(permissions);
    } catch (error) {
      console.log('Permission check limited:', error);
    }
  };

  const fetchThreats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/cybershield/threats`, { headers });
      setThreats(response.data.threats || emergingThreats);
    } catch {
      setThreats(emergingThreats);
    }
  };

  // GPS Reminder Functions
  const fetchGpsReminders = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/cybershield/gps-reminders`);
      setGpsReminders(response.data.reminders || []);
      setGpsReminderActive(response.data.reminders?.length > 0);
    } catch (error) {
      console.error('Failed to fetch GPS reminders');
    }
  }, [user]);

  const checkDueReminders = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/cybershield/gps-reminders/check`);
      if (response.data.has_notifications) {
        response.data.notifications.forEach(notif => {
          toast.warning(notif.message, {
            duration: 10000,
            icon: '🛰️',
            description: `Your ${notif.duration_was} minute GPS session is complete`
          });
        });
        fetchGpsReminders();
      }
    } catch (error) {
      console.error('Failed to check reminders');
    }
  }, [user, fetchGpsReminders]);

  useEffect(() => {
    if (user) {
      fetchGpsReminders();
      // Check for due reminders every 30 seconds
      const interval = setInterval(checkDueReminders, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchGpsReminders, checkDueReminders]);

  const setGpsReminder = async () => {
    setSettingReminder(true);
    try {
      const response = await axios.post(`${API}/cybershield/gps-reminder`, {
        duration_minutes: gpsReminderDuration,
        note: gpsReminderNote || `Turn off location after ${gpsReminderDuration} minutes`
      });
      toast.success(response.data.message);
      setShowReminderModal(false);
      setGpsReminderNote('');
      fetchGpsReminders();
    } catch (error) {
      toast.error('Failed to set reminder');
    } finally {
      setSettingReminder(false);
    }
  };

  const cancelGpsReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/cybershield/gps-reminder/${reminderId}`);
      toast.success('Reminder cancelled');
      fetchGpsReminders();
    } catch (error) {
      toast.error('Failed to cancel reminder');
    }
  };

  const extendGpsReminder = async (reminderId, additionalMinutes = 15) => {
    try {
      const response = await axios.put(`${API}/cybershield/gps-reminder/${reminderId}/extend?additional_minutes=${additionalMinutes}`);
      toast.success(response.data.message);
      fetchGpsReminders();
    } catch (error) {
      toast.error('Failed to extend reminder');
    }
  };

  const formatTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;
    if (diff <= 0) return 'Due now';
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const startScan = async () => {
    if (!user?.is_premium && scanComplete) {
      toast.error('Premium required for additional scans');
      return;
    }

    setScanning(true);
    setScanProgress(0);
    setScanComplete(false);
    setScanResults(null);

    // Simulate comprehensive scan
    const scanSteps = [
      { progress: 10, label: "Checking browser permissions..." },
      { progress: 25, label: "Analyzing installed app patterns..." },
      { progress: 40, label: "Detecting location trackers..." },
      { progress: 55, label: "Scanning for data exfiltration risks..." },
      { progress: 70, label: "Checking network vulnerabilities..." },
      { progress: 85, label: "Correlating with threat intelligence..." },
      { progress: 100, label: "Generating report..." },
    ];

    for (const step of scanSteps) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setScanProgress(step.progress);
    }

    // Generate scan results
    const results = generateScanResults();
    setScanResults(results);
    setScanning(false);
    setScanComplete(true);

    // Save scan to backend
    try {
      await axios.post(`${API}/cybershield/scan`, { results });
    } catch (error) {
      console.log('Could not save scan results');
    }

    toast.success('Scan complete! Review your results below.');
  };

  const generateScanResults = () => {
    // Analyze actual browser permissions
    const browserRisks = [];
    if (browserPermissions.location === 'granted') {
      browserRisks.push({ type: 'Location Access', risk: 'high', description: 'Browser has location permission granted' });
    }
    if (browserPermissions.camera === 'granted') {
      browserRisks.push({ type: 'Camera Access', risk: 'medium', description: 'Browser can access camera' });
    }
    if (browserPermissions.microphone === 'granted') {
      browserRisks.push({ type: 'Microphone Access', risk: 'medium', description: 'Browser can access microphone' });
    }

    // Simulate app detection based on common patterns
    const detectedApps = trackingAppsDatabase
      .sort(() => Math.random() - 0.5)
      .slice(0, user?.is_premium ? 10 : 3);

    const riskScore = Math.floor(Math.random() * 30) + (user?.is_premium ? 40 : 60);

    return {
      overallScore: 100 - riskScore,
      riskLevel: riskScore > 70 ? 'critical' : riskScore > 50 ? 'high' : riskScore > 30 ? 'medium' : 'low',
      browserPermissions: browserRisks,
      potentialTrackers: detectedApps,
      recommendations: [
        { priority: 'high', action: 'Review location permissions for all apps', impact: 'Prevents constant GPS tracking' },
        { priority: 'high', action: 'Reset your advertising ID', impact: 'Breaks cross-app tracking profiles' },
        { priority: 'medium', action: 'Disable Bluetooth when not in use', impact: 'Prevents beacon tracking in stores' },
        { priority: 'medium', action: 'Use a VPN on public Wi-Fi', impact: 'Encrypts your network traffic' },
        { priority: 'low', action: 'Clear cookies regularly', impact: 'Reduces web tracking' },
      ],
      scanTime: new Date().toISOString(),
      threatsCorrelated: emergingThreats.slice(0, 3)
    };
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="cybershield-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00FF94]/20 to-blue-500/20 border border-[#00FF94]/30 mb-4">
            <Shield className="w-10 h-10 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Cyber<span className="text-[#00FF94]">Shield</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Scan your device for tracking apps, location leaks, and data exfiltration risks. 
            Stay informed about emerging cyber threats.
          </p>
          {!user?.is_premium && (
            <Badge className="mt-4 bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20">
              Free Preview - Upgrade for Full Scans
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#0a0a0a] border border-[#262626] p-1 grid grid-cols-4 w-full max-w-2xl mx-auto">
            <TabsTrigger value="scan" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black">
              <Scan className="w-4 h-4 mr-2" />
              Scan
            </TabsTrigger>
            <TabsTrigger value="threats" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" />
              Threats
            </TabsTrigger>
            <TabsTrigger value="gps" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black relative">
              <MapPin className="w-4 h-4 mr-2" />
              GPS Timer
              {gpsReminders.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                  {gpsReminders.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="guide" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black">
              <Settings className="w-4 h-4 mr-2" />
              Guide
            </TabsTrigger>
          </TabsList>

          {/* SCAN TAB */}
          <TabsContent value="scan" className="space-y-6">
            
            {/* Scan Card */}
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardContent className="p-6">
                {!scanning && !scanComplete && (
                  <div className="text-center py-8">
                    <div className="w-32 h-32 mx-auto mb-6 relative">
                      <div className="absolute inset-0 rounded-full bg-[#00FF94]/10 animate-pulse" />
                      <div className="absolute inset-4 rounded-full bg-[#00FF94]/20 flex items-center justify-center">
                        <Scan className="w-12 h-12 text-[#00FF94]" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Ready to Scan</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                      CyberShield will analyze your device for tracking apps, location leaks, 
                      and correlate findings with current cyber threats.
                    </p>
                    <Button onClick={startScan} className="btn-primary px-8 py-3">
                      <Scan className="w-5 h-5 mr-2" />
                      Start Security Scan
                    </Button>
                    
                    <div className="mt-6 text-xs text-gray-600">
                      <Info className="w-4 h-4 inline mr-1" />
                      Web-based scan analyzes browser permissions and common tracking patterns
                    </div>
                  </div>
                )}

                {scanning && (
                  <div className="text-center py-8">
                    <div className="w-32 h-32 mx-auto mb-6 relative">
                      <div className="absolute inset-0 rounded-full border-4 border-[#262626]" />
                      <div 
                        className="absolute inset-0 rounded-full border-4 border-[#00FF94] border-t-transparent animate-spin"
                        style={{ animationDuration: '1s' }}
                      />
                      <div className="absolute inset-4 rounded-full bg-[#0a0a0a] flex items-center justify-center">
                        <span className="text-2xl font-bold text-[#00FF94]">{scanProgress}%</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Scanning...</h3>
                    <Progress value={scanProgress} className="w-full max-w-md mx-auto h-2 mb-4" />
                    <p className="text-gray-500 text-sm">
                      {scanProgress < 25 && "Checking browser permissions..."}
                      {scanProgress >= 25 && scanProgress < 50 && "Analyzing tracking patterns..."}
                      {scanProgress >= 50 && scanProgress < 75 && "Detecting location risks..."}
                      {scanProgress >= 75 && "Correlating with threat intelligence..."}
                    </p>
                  </div>
                )}

                {scanComplete && scanResults && (
                  <div className="space-y-6">
                    {/* Overall Score */}
                    <div className="text-center">
                      <div className={`text-6xl font-bold mb-2 ${getScoreColor(scanResults.overallScore)}`}>
                        {scanResults.overallScore}
                      </div>
                      <p className="text-gray-500">Privacy Score</p>
                      <Badge className={`mt-2 ${getRiskColor(scanResults.riskLevel)}`}>
                        {scanResults.riskLevel.toUpperCase()} RISK
                      </Badge>
                    </div>

                    {/* Browser Permissions */}
                    <div className="bg-[#171717] rounded-lg p-4">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#00FF94]" />
                        Browser Permissions Detected
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(browserPermissions).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between text-sm">
                            <span className="text-gray-400 capitalize">{key}</span>
                            <Badge className={value === 'granted' ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}>
                              {value}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Detected Trackers */}
                    <div>
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Eye className="w-4 h-4 text-[#00FF94]" />
                        Potential Tracking Apps
                        {!user?.is_premium && (
                          <Badge className="bg-[#00FF94]/10 text-[#00FF94] text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            3 of 10 shown
                          </Badge>
                        )}
                      </h4>
                      <div className="grid gap-3">
                        {scanResults.potentialTrackers.map((app, index) => (
                          <div key={index} className="bg-[#171717] rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{app.icon}</span>
                              <div>
                                <p className="text-white font-medium">{app.name}</p>
                                <p className="text-xs text-gray-500">{app.trackers.join(', ')}</p>
                              </div>
                            </div>
                            <Badge className={getRiskColor(app.risk)}>
                              {app.risk}
                            </Badge>
                          </div>
                        ))}
                        
                        {!user?.is_premium && (
                          <div className="bg-gradient-to-r from-[#00FF94]/5 to-transparent border border-[#00FF94]/20 rounded-lg p-4 text-center">
                            <Lock className="w-6 h-6 text-[#00FF94] mx-auto mb-2" />
                            <p className="text-sm text-gray-400">
                              Upgrade to Premium to see all {trackingAppsDatabase.length} detected trackers
                            </p>
                            <Button className="btn-primary mt-3" size="sm" onClick={() => window.location.href = '/pricing'}>
                              <Crown className="w-4 h-4 mr-2" />
                              Unlock Full Report
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Re-scan Button */}
                    <div className="text-center pt-4">
                      <Button 
                        onClick={startScan} 
                        variant="outline" 
                        className="border-[#262626]"
                        disabled={!user?.is_premium}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {user?.is_premium ? 'Run New Scan' : 'Premium Required for Re-scan'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* THREATS TAB */}
          <TabsContent value="threats" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Emerging Cyber Threats</h3>
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <Radio className="w-3 h-3 mr-1 animate-pulse" />
                Live Feed
              </Badge>
            </div>

            {threats.length === 0 ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading threat intelligence...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {threats.map((threat, index) => (
                <Card key={index} className="bg-[#0a0a0a] border-[#262626]">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        threat.severity === 'critical' ? 'bg-red-500/20' :
                        threat.severity === 'high' ? 'bg-orange-500/20' : 'bg-yellow-500/20'
                      }`}>
                        <AlertTriangle className={`w-5 h-5 ${
                          threat.severity === 'critical' ? 'text-red-400' :
                          threat.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-medium">{threat.title}</h4>
                          <Badge className={getRiskColor(threat.severity)}>
                            {threat.severity}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 mb-3">{threat.description}</p>
                        <div className="flex flex-wrap gap-4 text-xs">
                          <span className="text-gray-600">
                            <strong className="text-gray-400">Affected:</strong> {threat.affected}
                          </span>
                        </div>
                        
                        {user?.is_premium ? (
                          <div className="mt-3 p-3 bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg">
                            <p className="text-xs text-[#00FF94] font-medium mb-1">Recommended Action:</p>
                            <p className="text-xs text-gray-400">{threat.solution}</p>
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-[#171717] rounded-lg flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              <Lock className="w-3 h-3 inline mr-1" />
                              Solution locked
                            </span>
                            <Badge className="bg-[#00FF94]/10 text-[#00FF94] text-xs cursor-pointer">
                              Upgrade
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </TabsContent>

          {/* GPS TIMER TAB */}
          <TabsContent value="gps" className="space-y-6" data-testid="gps-timer-tab">
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#00FF94]" />
                  Location/GPS Reminder
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Set a reminder to turn off location services after a specified time. Perfect for when you temporarily enable GPS for maps or ride-sharing.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Set New Reminder */}
                <div className="bg-[#171717] rounded-lg p-6 border border-[#262626]">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Timer className="w-5 h-5 text-[#00FF94]" />
                    Set GPS Timer
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Duration Selector */}
                    <div>
                      <label className="text-sm text-gray-400 block mb-2">Remind me in:</label>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setGpsReminderDuration(Math.max(5, gpsReminderDuration - 5))}
                          className="w-10 h-10 rounded-full bg-[#262626] text-white flex items-center justify-center hover:bg-[#00FF94]/20"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <div className="flex-1 text-center">
                          <span className="text-3xl font-bold text-[#00FF94]">{gpsReminderDuration}</span>
                          <span className="text-gray-400 ml-2">minutes</span>
                        </div>
                        <button
                          onClick={() => setGpsReminderDuration(Math.min(240, gpsReminderDuration + 5))}
                          className="w-10 h-10 rounded-full bg-[#262626] text-white flex items-center justify-center hover:bg-[#00FF94]/20"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Quick Presets */}
                    <div className="flex flex-wrap gap-2">
                      {[15, 30, 60, 120].map(mins => (
                        <button
                          key={mins}
                          onClick={() => setGpsReminderDuration(mins)}
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                            gpsReminderDuration === mins
                              ? 'bg-[#00FF94] text-black font-semibold'
                              : 'bg-[#262626] text-gray-400 hover:bg-[#00FF94]/20'
                          }`}
                        >
                          {mins < 60 ? `${mins} min` : `${mins/60} hr`}
                        </button>
                      ))}
                    </div>

                    {/* Optional Note */}
                    <div>
                      <label className="text-sm text-gray-400 block mb-2">Note (optional):</label>
                      <input
                        type="text"
                        value={gpsReminderNote}
                        onChange={(e) => setGpsReminderNote(e.target.value)}
                        placeholder="e.g., Using Uber, turn off location after"
                        className="w-full px-4 py-2 bg-[#262626] border border-[#363636] rounded-lg text-white placeholder-gray-500 focus:border-[#00FF94] focus:outline-none"
                      />
                    </div>

                    {/* Set Button */}
                    <Button 
                      onClick={setGpsReminder}
                      disabled={settingReminder}
                      className="w-full btn-primary"
                    >
                      {settingReminder ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Setting...
                        </>
                      ) : (
                        <>
                          <BellRing className="w-4 h-4 mr-2" />
                          Set Location Reminder
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Active Reminders */}
                {gpsReminders.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-400" />
                      Active Reminders
                    </h3>
                    {gpsReminders.map((reminder) => (
                      <div 
                        key={reminder.id}
                        className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">
                              {reminder.note || 'GPS Reminder'}
                            </p>
                            <p className="text-sm text-yellow-400">
                              <Timer className="w-3 h-3 inline mr-1" />
                              {formatTimeRemaining(reminder.end_time)} remaining
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => extendGpsReminder(reminder.id, 15)}
                            className="text-yellow-400 hover:bg-yellow-500/20"
                          >
                            +15m
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancelGpsReminder(reminder.id)}
                            className="text-red-400 hover:bg-red-500/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tips */}
                <div className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg p-4">
                  <h4 className="text-[#00FF94] font-semibold mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Why use GPS Timer?
                  </h4>
                  <ul className="text-sm text-gray-400 space-y-2">
                    <li>• <strong>Uber/Lyft trips:</strong> Set 30 min reminder, turn off location after arrival</li>
                    <li>• <strong>Google Maps navigation:</strong> Set timer matching your ETA</li>
                    <li>• <strong>Food delivery:</strong> Enable location briefly for order updates</li>
                    <li>• <strong>Location check-ins:</strong> Share location temporarily, get reminded to disable</li>
                  </ul>
                </div>

                {/* Privacy Info */}
                <div className="text-center text-sm text-gray-500 pt-4 border-t border-[#262626]">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-yellow-500" />
                  Location data is one of the most valuable pieces of information tracked about you.
                  Fog Reveal and similar tools purchase this data to track individuals without warrants.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FIX GUIDE TAB */}
          <TabsContent value="guide" className="space-y-6">
            <h3 className="text-lg font-bold text-white mb-4">Step-by-Step Privacy Fixes</h3>
            
            {/* iOS Guide */}
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-2xl">🍎</span>
                  iPhone / iPad
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="text-white text-sm font-medium">Disable App Tracking</p>
                      <p className="text-xs text-gray-500">Settings → Privacy → Tracking → Disable "Allow Apps to Request to Track"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="text-white text-sm font-medium">Review Location Services</p>
                      <p className="text-xs text-gray-500">Settings → Privacy → Location Services → Set apps to "While Using" or "Never"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="text-white text-sm font-medium">Reset Advertising Identifier</p>
                      <p className="text-xs text-gray-500">Settings → Privacy → Apple Advertising → Reset Identifier</p>
                    </div>
                  </div>
                  {user?.is_premium && (
                    <>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">4</div>
                        <div>
                          <p className="text-white text-sm font-medium">Enable Private Relay</p>
                          <p className="text-xs text-gray-500">Settings → Apple ID → iCloud → Private Relay → Enable</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">5</div>
                        <div>
                          <p className="text-white text-sm font-medium">Use Hide My Email</p>
                          <p className="text-xs text-gray-500">Settings → Apple ID → iCloud → Hide My Email → Create new addresses</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {!user?.is_premium && (
                  <div className="p-3 bg-[#171717] rounded-lg text-center">
                    <Lock className="w-4 h-4 text-gray-500 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">2 more advanced steps available for Premium</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Android Guide */}
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-2xl">🤖</span>
                  Android
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="text-white text-sm font-medium">Delete Advertising ID</p>
                      <p className="text-xs text-gray-500">Settings → Privacy → Ads → Delete advertising ID</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="text-white text-sm font-medium">Review App Permissions</p>
                      <p className="text-xs text-gray-500">Settings → Apps → [App Name] → Permissions → Remove location access</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="text-white text-sm font-medium">Disable Google Location History</p>
                      <p className="text-xs text-gray-500">Google Account → Data & Privacy → Location History → Turn Off</p>
                    </div>
                  </div>
                  {user?.is_premium && (
                    <>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">4</div>
                        <div>
                          <p className="text-white text-sm font-medium">Use Private DNS</p>
                          <p className="text-xs text-gray-500">Settings → Network → Private DNS → Set to dns.adguard.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xs font-bold flex-shrink-0">5</div>
                        <div>
                          <p className="text-white text-sm font-medium">Disable Nearby Device Scanning</p>
                          <p className="text-xs text-gray-500">Settings → Google → Devices & Sharing → Disable all scanning</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {!user?.is_premium && (
                  <div className="p-3 bg-[#171717] rounded-lg text-center">
                    <Lock className="w-4 h-4 text-gray-500 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">2 more advanced steps available for Premium</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Upgrade CTA for Free Users */}
        {!user?.is_premium && (
          <Card className="bg-gradient-to-r from-[#00FF94]/10 to-blue-500/10 border-[#00FF94]/20 mt-8">
            <CardContent className="p-6 text-center">
              <Crown className="w-10 h-10 text-[#00FF94] mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-2">Unlock Full CyberShield</h3>
              <p className="text-gray-400 text-sm mb-4 max-w-md mx-auto">
                Get unlimited scans, full tracker reports, threat solutions, and advanced privacy guides.
              </p>
              <Button className="btn-primary" onClick={() => window.location.href = '/pricing'}>
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
